import type { RecapItem } from "../../lib/api";
import { BAND_PILL, percentileLabel } from "../inbox/labels";
import { founderLine, positionLabel, recapReceiptsLine } from "./labels";

/** The state line a card carries once the owner has answered. Nothing otherwise. */
export const REPLIED_LINE = "You opened the door — the two of you are talking.";

/** The card's one way out, and the only verb the briefing still has. */
export const OPEN_CONVERSATION = "Open the conversation";

type Props = {
  item: RecapItem;
  /** Open this knock's thread in the console. The card does nothing else. */
  onOpen: () => void;
};

/**
 * One item of the Morning Nob (FR-7.3) — A SUMMARY, WITH ONE WAY IN (owner,
 * 2026-08-26).
 *
 * It used to carry the inbox's whole action row — Reply, Book, Save, Not for me
 * — plus a strip asking whether the ranking was helpful, each with its own
 * thirty-second undo. That made the briefing a second inbox with a second reply
 * composer and a second set of verbs, sitting a few pixels above the console
 * where the same knock is a conversation with the same verbs in it. Two places
 * to answer one person is one too many, and the briefing is the wrong one: it
 * is what you READ over coffee, not what you work in.
 *
 * So the card states the case and gets out of the way: who, why Nob surfaced it,
 * the dry receipts, whether the door is already open — and one affordance that
 * takes the owner to the conversation, where every verb lives.
 */
export function RecapItemCard({ item, onOpen }: Props) {
  const sub = founderLine(item.sender);

  return (
    <li className="rounded-2xl border border-border/80 bg-surface/20 p-5 sm:p-6">
      <div className="flex items-start gap-5">
        <span className="mt-1 font-mono text-sm tabular-nums text-fainter" aria-hidden>
          {positionLabel(item.position)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="truncate text-[15px] font-medium text-text">
                {item.sender.display_name}
              </p>
              {sub && <p className="mt-0.5 text-[13px] leading-relaxed text-faint">{sub}</p>}
            </div>
            <span
              className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 font-mono
                          text-[11px] uppercase tracking-[0.08em] ${BAND_PILL[item.standing.band]}`}
            >
              {percentileLabel(item.standing.percentile)}
            </span>
          </div>

          {/* The why-surfaced one-liner is the reason it made the cut (FR-7.3):
              the summary IS the grounding, so it carries the label and the item. */}
          {item.summary && (
            <div className="mt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fainter">
                Why Nob surfaced it
              </p>
              <p className="mt-2 text-[1.0625rem] leading-relaxed text-text">
                &ldquo;{item.summary}&rdquo;
              </p>
            </div>
          )}

          <p className="mt-4 font-mono text-[11px] tabular-nums text-faint">
            {recapReceiptsLine(item.receipts)}
          </p>

          {/* The one state a summary owes: the door is open, and where. Said
              only when it is true — a line reading "you have not replied" would
              be the briefing nagging, which is what the verbs used to do. */}
          {item.replied && (
            <p className="mt-3 text-[13px] leading-relaxed text-accent-ink-strong/90">
              {REPLIED_LINE}
            </p>
          )}

          <div className="mt-5 border-t border-border-soft pt-4">
            <button
              type="button"
              onClick={onOpen}
              className="text-sm font-medium text-accent-ink transition-colors duration-75
                         hover:text-accent-ink-strong focus-visible:outline-none
                         focus-visible:ring-2 focus-visible:ring-focus/70
                         focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded"
            >
              {OPEN_CONVERSATION} <span aria-hidden="true">&rarr;</span>
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}
